import { TbBuildingBank } from 'react-icons/tb'

import { TopMenu } from '../Components/TopMenu'
import { legalEntityFormTemplate } from '../Components/LegalEntityStampHint'
import { serviceFields } from '../Contracts/DictionaryFields'
import { CheckDictionaryPageAccessTask } from '../Tasks/CheckDictionaryPageAccessTask'

import type { IResourceConfig } from '@/Core/Contracts/UI'
import type { IFormEventContext } from '@/Core/Contracts/UI/Form'

/** Мінімум, який нас цікавить у значенні файлового поля. */
interface StampFileLike {
    uuid?: string
    createdAt?: string
}

/**
 * Довідник «Юридичні особи» (`SPEC-007` §4 № 15, §5.3, §7.4–§7.5).
 *
 * Експедитор у картках клієнта й перевізника, у договорах і друкованих
 * формах. Поля й підписи — дослівно з легасі (`ListLegalResource.js`), у тому
 * самому порядку.
 *
 * Печатка — двокрокове завантаження (G-05): ядровий `FileInput` одразу вантажить
 * PNG на `POST /services/legal_entities/stamps` (сирота), а збереження форми
 * прикріплює його через `fileUuids`. У легасі печатку можна було завантажити
 * лише в уже збережену юрособу — тепер і під час створення.
 *
 * Сторінку бачить лише адміністратор (`pageConfig.accessFn`, `SPEC-007` §7.1,
 * Q-06 (а)): бухгалтер має право на юрособи лише в API, читати їх для селектів
 * може кожен.
 */
export const ListLegalEntityResource: IResourceConfig = {
    title: 'Юридичні особи',
    fields: {
        title: {
            label: 'Назва',
            type: 'string',
            isRequired: true,
        },
        fullName: {
            label: 'Повна назва',
            type: 'string',
            isRequired: true,
        },
        formTaxation: {
            label: 'Форма оподаткування',
            type: 'select',
            resource: 'Setting.ListFormTaxation',
            isRequired: true,
        },
        address: {
            label: 'Адреса',
            type: 'string',
            isRequired: true,
        },
        iban: {
            label: 'Рахунок IBAN',
            type: 'string',
            isRequired: true,
        },
        bank: {
            label: 'Банк рахунку',
            type: 'string',
            isRequired: true,
        },
        mfo: {
            label: 'МФО банку рахунку',
            type: 'string',
            isRequired: true,
        },
        edrpou: {
            label: 'Код ЄДРПОУ',
            type: 'string',
            isRequired: true,
        },
        numberVAT: {
            label: 'Номер витягу платника ПДВ',
            type: 'string',
        },
        ipn: {
            label: 'ІПН',
            type: 'string',
            isRequired: true,
        },
        manager: {
            label: 'ПІБ директора',
            type: 'string',
            isRequired: true,
        },
        onBasis: {
            label: 'Діє на підставі',
            type: 'string',
            isRequired: true,
        },
        email: {
            label: 'E-mail',
            type: 'string',
            isRequired: true,
        },
        phone: {
            label: 'Телефон',
            type: 'phone',
            isRequired: true,
        },
        stamp: {
            label: 'З печаткою та підписом',
            type: 'file',
            // Одна печатка: нова замінює попередню, бек прибирає стару з бази
            // й сховища (R-19)
            isMultiple: false,
            uploadUrl: '/services/legal_entities/stamps',
            uploadAccept: ['png'],
            // Стеля — `FileUploadValidator::MAX_SIZE_BYTES` бека (nginx ядра
            // пропускає тіло до 10 МБ)
            uploadMaxSizeMb: 10,
        },
        sorting: {
            label: 'Сортування',
            type: 'number',
            defaultValue: 500,
        },
        ...serviceFields(),
    },
    /**
     * Двокроковий attach: щойно завантажену печатку мапимо в `fileUuids`
     * тіла POST/PATCH і прибираємо поле `stamp` — інакше save-цикл ядра
     * згорнув би його в IRI, а бек приймає печатку лише через `fileUuids`.
     *
     * Прикріплюємо ЛИШЕ новий upload. Ознака — `createdAt`: воно є лише у
     * відповіді на завантаження (група `file:read`), а вже прикріплена
     * печатка приходить у картці групою `file:short:read`, де його немає.
     */
    form: {
        // Підказка про печатку — над її полем у формі (`SPEC-007` §12)
        template: legalEntityFormTemplate,
        eventHandlers: {
            onBeforeTransformSendSaveRequest: {
                legalEntityStampFileUuids: ({ values, formContext }: IFormEventContext): void => {
                    const toArray = (value: unknown): unknown[] =>
                        Array.isArray(value) ? value : value != null ? [value] : []

                    const byUuid = new Map<string, StampFileLike>()

                    for (const raw of [...toArray(formContext?.getInitialValue?.('stamp')), ...toArray(values['stamp'])]) {
                        const file: StampFileLike = typeof raw === 'object' && raw !== null ? raw : {}
                        const uuid = file.uuid ?? (typeof raw === 'string' ? raw : undefined)

                        if (typeof uuid === 'string' && uuid.length > 0) {
                            byUuid.set(uuid, file)
                        }
                    }

                    values['fileUuids'] = Array.from(byUuid.values())
                        .filter((file) => Boolean(file.createdAt))
                        .map((file) => file.uuid)
                        .filter((uuid): uuid is string => typeof uuid === 'string')

                    delete values['stamp']
                },
            },
        },
    },
    // Колонки легасі (`SPEC-007` §7.4) — поля в порядку оголошення
    table: {
        showColumns: [
            'title',
            'fullName',
            'formTaxation',
            'address',
            'iban',
            'bank',
            'mfo',
            'edrpou',
            'numberVAT',
            'ipn',
            'manager',
            'onBasis',
            'email',
            'phone',
            'stamp',
            'sorting',
        ],
    },
    // Фільтри легасі: Ід · Назва · Повна назва · Телефон
    filter: {
        includeFields: ['id', 'title', 'fullName', 'phone'],
    },
    places: {
        top: <TopMenu current="legal-entities" />,
    },
    restUrl: '/services/legal_entities',
    pageUrl: '/settings/legal-entities',
    pageConfig: {
        title: 'Юридичні особи',
        icon: <TbBuildingBank />,
        accessFn: CheckDictionaryPageAccessTask,
    },
    settings: {
        // Пошук у селектах CRM-форм (легасі `_search`: назва й повна назва, §6.4)
        searchFields: ['id', 'title', 'fullName'],
    },
}
